import { useEffect } from 'react';
import { useSearchParams } from 'react-router';
import {
  isClientWorkspaceTab,
  useClientWorkspaceStore,
} from '@hero/hooks-shared';

const CLIENT_PARAM = 'clientId';
const TAB_PARAM = 'tab';
const DEFAULT_TAB = 'info';

/**
 * Bidirectionally syncs the client workspace store with URL search params:
 *
 * - `?clientId=...` <-> `selectedClientId`
 * - `?tab=...` <-> `activeTab` (omitted when `info` to keep URLs clean)
 *
 * Mount this once at the workspace root so:
 * - Deep-links like `/crm?clientId=abc&tab=pricing` hydrate the store.
 * - Picking a client / switching tabs persists in the URL (replace history).
 *
 * Other state (`search`, `roleFilter`, `editingSection`, `isCreateOpen`) is
 * intentionally ephemeral and stays in the store only.
 *
 * Web-only: lives in `@hero/web-hooks` because `react-router` is browser-only
 * and Metro (React Native) cannot statically analyse its dynamic `import()`
 * for route-module loading. The store itself stays in `@hero/hooks-shared` so
 * mobile screens can read/write the same state without pulling in a router.
 */
export function useClientWorkspaceUrlSync(): void {
  const [searchParams, setSearchParams] = useSearchParams();

  const selectedClientId = useClientWorkspaceStore(s => s.selectedClientId);
  const activeTab = useClientWorkspaceStore(s => s.activeTab);
  const selectClient = useClientWorkspaceStore(s => s.selectClient);
  const setActiveTab = useClientWorkspaceStore(s => s.setActiveTab);

  // URL -> store
  useEffect(() => {
    const urlClientId = searchParams.get(CLIENT_PARAM) ?? undefined;
    const rawTab = searchParams.get(TAB_PARAM);
    const urlTab = isClientWorkspaceTab(rawTab) ? rawTab : undefined;

    if (urlClientId !== selectedClientId) {
      selectClient(urlClientId);
    }
    if (urlTab && urlTab !== activeTab) {
      setActiveTab(urlTab);
    } else if (!urlTab && activeTab !== DEFAULT_TAB && !urlClientId) {
      // No selection AND no tab param -> reset to info.
      setActiveTab(DEFAULT_TAB);
    }
    // We deliberately depend only on `searchParams` so external nav (the
    // drawer's "Open full" button, browser back/forward) is honoured. Reading
    // `selectedClientId` / `activeTab` here is a guard, not a trigger.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams]);

  // Store -> URL
  useEffect(() => {
    const urlClientId = searchParams.get(CLIENT_PARAM);
    const urlTab = searchParams.get(TAB_PARAM);

    const desiredClientId = selectedClientId ?? null;
    const desiredTab = activeTab !== DEFAULT_TAB ? activeTab : null;

    if (urlClientId === desiredClientId && urlTab === desiredTab) {
      return;
    }

    setSearchParams(
      prev => {
        const next = new URLSearchParams(prev);
        if (desiredClientId) next.set(CLIENT_PARAM, desiredClientId);
        else next.delete(CLIENT_PARAM);
        if (desiredTab) next.set(TAB_PARAM, desiredTab);
        else next.delete(TAB_PARAM);
        return next;
      },
      { replace: true },
    );
    // Same rationale as above: only react to selection/tab changes.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedClientId, activeTab]);
}
